import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { NewsRow } from '../../../../lib/supabase';

const NEWS_CATEGORIES = ['公司公告', '產品資訊', '產業動態'] as const;

interface Props {
  news: NewsRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function NewsFormModal({ news, onClose, onSaved }: Props) {
  const isEdit = news !== null;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category: '公司公告',
    date: new Date().toISOString().split('T')[0],
    title: '',
    excerpt: '',
    content: '',
    tags: '',
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    if (isEdit && news) {
      setForm({
        category: news.category,
        date: news.date,
        title: news.title,
        excerpt: news.excerpt,
        content: news.content,
        tags: news.tags.join(', '),
        is_active: news.is_active,
        sort_order: news.sort_order,
      });
    }
  }, [isEdit, news]);

  const set = (key: string, value: string | boolean | number) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      category: form.category,
      date: form.date,
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content.trim(),
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      is_active: form.is_active,
      sort_order: Number(form.sort_order),
      updated_at: new Date().toISOString(),
    };
    if (isEdit && news) {
      await supabase.from('news').update(payload).eq('id', news.id);
    } else {
      await supabase.from('news').insert(payload);
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-xl h-full flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-800">
            {isEdit ? '編輯消息' : '新增消息'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Category + Date */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">分類 *</label>
              <select
                value={form.category}
                onChange={e => set('category', e.target.value)}
                className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5] bg-white cursor-pointer"
              >
                {NEWS_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="w-40">
              <label className="block text-xs font-medium text-gray-600 mb-1">日期 *</label>
              <input
                type="date"
                value={form.date}
                onChange={e => set('date', e.target.value)}
                className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">標題 *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="請輸入消息標題"
              className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">摘要</label>
            <textarea
              value={form.excerpt}
              onChange={e => set('excerpt', e.target.value)}
              rows={2}
              maxLength={200}
              placeholder="簡短說明（顯示在列表頁）"
              className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5] resize-none"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">內文</label>
            <textarea
              value={form.content}
              onChange={e => set('content', e.target.value)}
              rows={8}
              maxLength={500}
              placeholder="詳細內容"
              className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5] resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.content.length}/500</p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">標籤</label>
            <input
              type="text"
              value={form.tags}
              onChange={e => set('tags', e.target.value)}
              placeholder="用逗號分隔，例：防爆, ATEX, 新品"
              className="w-full text-sm border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
            />
          </div>

          {/* Status + Sort */}
          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => set('is_active', e.target.checked)}
                className="accent-[#1a73b5] w-4 h-4"
              />
              <span className="text-sm text-gray-700">上架顯示</span>
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">排序</span>
              <input
                type="number"
                value={form.sort_order}
                onChange={e => set('sort_order', Number(e.target.value))}
                className="w-16 text-sm border border-gray-200 px-2 py-1.5 focus:outline-none focus:border-[#1a73b5] text-center"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-2 text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.title.trim()}
            className="flex-1 py-2 text-sm bg-[#1a73b5] text-white hover:bg-[#1558a0] cursor-pointer disabled:opacity-60 whitespace-nowrap"
          >
            {saving ? '儲存中...' : (isEdit ? '儲存變更' : '新增消息')}
          </button>
        </div>
      </div>
    </div>
  );
}
